import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, AlertCircle, CheckCircle } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import './AuthPages.css';

/**
 * /reset-password?token=... — set a new password from an emailed link.
 * Token is single-use with a 60-minute expiry. The page pre-validates the
 * token on load (GET /auth/reset-password/check) so a dead link is rejected
 * immediately rather than after the user has typed a new password.
 */
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // 'checking' | 'valid' | 'invalid'
  const [linkState, setLinkState] = useState(token ? 'checking' : 'invalid');
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get('/auth/reset-password/check', { params: { token } })
      .then(res => { if (!cancelled) setLinkState(res.data?.valid ? 'valid' : 'invalid'); })
      .catch(() => { if (!cancelled) setLinkState('valid'); }); // network hiccup → let submit decide
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post('/auth/reset-password', { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const shell = (children) => (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo-wrap">
          <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
        </div>
        {children}
      </div>
    </div>
  );

  if (linkState === 'checking') {
    return shell(
      <>
        <h1>Reset your password</h1>
        <p className="auth-subtitle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <span className="spinner" /> Checking your link…
        </p>
      </>
    );
  }

  if (linkState === 'invalid') {
    return shell(
      <>
        <h1>Reset your password</h1>
        <div className="auth-error">
          <AlertCircle size={16} /> This reset link is invalid or has expired. Links last 60 minutes and can only be used once.
        </div>
        <p className="auth-switch">
          <Link to="/forgot-password">Request a new link</Link>
        </p>
      </>
    );
  }

  return shell(
    <>
      <h1>Choose a new password</h1>

      {done ? (
        <>
          <p className="auth-subtitle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <CheckCircle size={18} style={{ color: 'var(--green)', flexShrink: 0 }} />
            Password updated.
          </p>
          <p className="auth-switch">
            <Link to="/login">Sign in with your new password</Link>
          </p>
        </>
      ) : (
        <>
          <p className="auth-subtitle">At least 8 characters.</p>

          {error && (
            <div className="auth-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="label">New password</label>
              <PasswordInput placeholder="••••••••"
                value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                required autoFocus autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label className="label">Confirm new password</label>
              <PasswordInput placeholder="••••••••"
                value={form.confirm} onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))}
                required autoComplete="new-password" />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? <span className="spinner" /> : <><KeyRound size={16} /> Update password</>}
            </button>
          </form>

          <p className="auth-switch">
            Link expired? <Link to="/forgot-password">Request a new one</Link>
          </p>
        </>
      )}
    </>
  );
};

export default ResetPasswordPage;
